import { d as debounce } from './debounce-CFN_LA0O.js';
import { s as setLocalization } from './setLocalization-7tixTteg.js';

class LocalizationSelector extends HTMLElement {
  connectedCallback() {
    this.search = this.querySelector('[data-localization-search]');
    this.groups = [...this.querySelectorAll('collapsible-element')];
    this.items = [...this.querySelectorAll('[data-country-item]')];
    this.noResults = this.querySelector('[data-localization-no-results]');

    // Remember which groups were open before a search so we can restore them.
    this.defaultOpen = new Map(this.groups.map((group) => [group, this.isOpen(group)]));

    this.search?.addEventListener('input', () => this.handleSearch());
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    // Drop a pending run so its closure stops retaining the country nodes.
    this.handleSearch.cancel();
    this.removeEventListener('click', this.handleClick);
  }

  /**
   * Record the picked market with Shopify rather than only landing on its URL,
   * so the choice survives a later visit to the root. The anchor stays the
   * crawlable, no-JS path — its `hreflang` already carries `{language}-{country}`.
   * @param {MouseEvent} event
   */
  handleClick = (event) => {
    const link = event.target.closest('a[hreflang]');
    if (!link) return;

    const [language, country] = link.hreflang.split('-');
    if (!language || !country) return;

    event.preventDefault();
    setLocalization(country, language, link.getAttribute('href'));
  };

  /**
   * Filter countries by name and toggle empty continent groups. Debounced so
   * the collapsible groups don't visibly jump open/closed on every keystroke.
   */
  handleSearch = debounce(() => {
    const query = this.search.value.trim().toLowerCase();

    this.items.forEach((item) => {
      const match = !query || item.dataset.countryName.includes(query);
      item.classList.toggle('hidden', !match);
    });

    let anyVisible = false;

    this.groups.forEach((group) => {
      const hasMatch = group.querySelector('[data-country-item]:not(.hidden)') !== null;
      group.classList.toggle('hidden', !hasMatch);
      if (hasMatch) anyVisible = true;

      // While searching, open whatever still has hits; on clear, restore each
      // group to the state it started in.
      this.setOpen(group, query ? hasMatch : this.defaultOpen.get(group));
    });

    this.noResults?.classList.toggle('hidden', anyVisible);
  }, 300);

  /**
   * @param {HTMLElement} group
   * @return {boolean}
   */
  isOpen(group) {
    const controller = group.querySelector('[aria-expanded]');
    return controller?.getAttribute('aria-expanded') === 'true';
  }

  /**
   * @param {HTMLElement} group
   * @param {boolean} shouldOpen
   */
  setOpen(group, shouldOpen) {
    if (this.isOpen(group) === shouldOpen) return;
    shouldOpen ? group.open?.() : group.close?.();
  }
}

customElements.define('localization-selector', LocalizationSelector);
